/**
 * **************************************************************************
 * Route State Service
 *
 * Centralized service to manage route-based state and provide observables
 * for components to react to route changes without duplicating logic
 * **************************************************************************
 */

import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RouteStateService {
  
  // BehaviorSubjects to hold current state
  private isWorkflowSummaryViewSubject = new BehaviorSubject<boolean>(false);
  private isWorkflowPrerequisiteViewSubject = new BehaviorSubject<boolean>(false);
  private isWorkflowExecutionHistoryViewSubject = new BehaviorSubject<boolean>(false);
  private currentUrlSubject = new BehaviorSubject<string>('');

  // Public observables for components to subscribe to
  public isWorkflowSummaryView$: Observable<boolean> = this.isWorkflowSummaryViewSubject.asObservable();
  public isWorkflowPrerequisiteView$: Observable<boolean> = this.isWorkflowPrerequisiteViewSubject.asObservable();
  public isWorkflowExecutionHistoryView$: Observable<boolean> = this.isWorkflowExecutionHistoryViewSubject.asObservable();
  public currentUrl$: Observable<string> = this.currentUrlSubject.asObservable();

  constructor(private router: Router) {
    this.initializeRouteTracking();
  }

  /**
   * Initialize route tracking by subscribing to router events
   */
  private initializeRouteTracking(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(event => (event as NavigationEnd).url)
      )
      .subscribe(url => {
        this.updateRouteState(url);
      });

    // Set initial state
    this.updateRouteState(this.router.url);
  }

  /**
   * Update route state based on current URL
   * @param url - Current router URL
   */
  private updateRouteState(url: string): void {
    this.currentUrlSubject.next(url);
    
    // Check for workflow summary view
    const isWorkflowSummaryView = url.includes('/wkfl-sum/view');
    this.isWorkflowSummaryViewSubject.next(isWorkflowSummaryView);
    
    // Check for workflow prerequisite view
    const isWorkflowPrerequisiteView = url.includes('/workflow-prerequisite');
    this.isWorkflowPrerequisiteViewSubject.next(isWorkflowPrerequisiteView);
    
    // Check for workflow execution history view
    const isWorkflowExecutionHistoryView = url.includes('/wf-job/job_history_table');
    this.isWorkflowExecutionHistoryViewSubject.next(isWorkflowExecutionHistoryView);
  }

  /**
   * Get current workflow summary view state (synchronous)
   * @returns boolean indicating if current route is workflow summary view
   */
  public get isWorkflowSummaryView(): boolean {
    return this.isWorkflowSummaryViewSubject.value;
  }

  /**
   * Get current workflow prerequisite view state (synchronous)
   * @returns boolean indicating if current route is workflow prerequisite view
   */
  public get isWorkflowPrerequisiteView(): boolean {
    return this.isWorkflowPrerequisiteViewSubject.value;
  }

  /**
   * Get current workflow execution history view state (synchronous)
   * @returns boolean indicating if current route is workflow execution history view
   */
  public get isWorkflowExecutionHistoryView(): boolean {
    return this.isWorkflowExecutionHistoryViewSubject.value;
  }

  /**
   * Get current URL (synchronous)
   * @returns string current router URL
   */
  public get currentUrl(): string {
    return this.currentUrlSubject.value;
  }

  /**
   * Check if current route matches a specific pattern
   * @param pattern - URL pattern to check against
   * @returns boolean indicating if current route matches the pattern
   */
  public isCurrentRouteMatching(pattern: string): boolean {
    return this.currentUrl.includes(pattern);
  }

  /**
   * Observable that emits true when route matches a specific pattern
   * @param pattern - URL pattern to check against
   * @returns Observable<boolean>
   */
  public isRouteMatching$(pattern: string): Observable<boolean> {
    return this.currentUrl$.pipe(
      map(url => url.includes(pattern))
    );
  }
}
